import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';

const PageWrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const Heading = styled.h1`
  font-size: 28px;
  margin: 0 0 8px;
  font-weight: 700;
  color: #0f172a;
`;

const Description = styled.p`
  margin: 0 0 24px;
  color: #475569;
  max-width: 640px;
`;

const DateCard = styled.section`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #ffffff, #f8fafc);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
`;

const DateInfo = styled.div`
  min-width: 220px;
`;

const DateEyebrow = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0284c7;
`;

const DateHeading = styled.div`
  margin-top: 6px;
  font-size: 22px;
  font-weight: 600;
  color: #0f172a;
`;

const DateSub = styled.div`
  margin-top: 2px;
  font-size: 14px;
  color: #64748b;
`;

const DateControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const DateInput = styled.input`
  height: 44px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid #cbd5f5;
  background: #ffffff;
  font-size: 14px;
  color: #0f172a;
  min-width: 180px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }
`;

const NavButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
  font-size: 20px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
    transform: none;
    box-shadow: none;
  }
`;

const TodayButton = styled.button`
  height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  border: none;
  background: #0ea5e9;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    background: #0284c7;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const StatGrid = styled.div`
  margin-top: 32px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div`
  padding: 20px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
`;

const StatLabel = styled.div`
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 600;
`;

const StatValue = styled.div`
  margin-top: 6px;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
`;

const StatHint = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #64748b;
`;

const ContentCard = styled.section`
  margin-top: 32px;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 15px 45px rgba(15, 23, 42, 0.05);
  padding: 24px;
`;

const SectionHeading = styled.h2`
  font-size: 20px;
  margin: 0 0 4px;
  color: #0f172a;
`;

const SectionSub = styled.p`
  margin: 0 0 20px;
  color: #64748b;
  font-size: 14px;
`;

const InfoBanner = styled.div`
  padding: 20px;
  border-radius: 14px;
  border: 1px dashed #cbd5f5;
  background: #f8fafc;
  color: #475569;
`;

const ErrorCard = styled(InfoBanner)`
  border-color: #fecaca;
  background: #fff1f2;
  color: #b91c1c;
`;

const SessionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SessionCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
`;

const SessionTime = styled.div`
  min-width: 110px;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
`;

const SessionBody = styled.div`
  flex: 1;
  min-width: 200px;
`;

const SessionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
`;

const SessionMeta = styled.div`
  margin-top: 6px;
  font-size: 14px;
  color: #475569;
`;

const SessionBadges = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const MetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  background: #f1f5f9;
  color: #0f172a;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) => p.$bg || 'rgba(14, 165, 233, 0.12)'};
  color: ${(p) => p.$color || '#0ea5e9'};
`;

const EmptyCard = styled(InfoBanner)`
  text-align: center;
  padding: 32px;
`;

const QuickTip = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  color: #94a3b8;
`;

const EventCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  border-left: 4px solid ${(p) => p.$color || '#6366f1'};
`;

const EventTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => p.$bg || 'rgba(99,102,241,0.12)'};
  color: ${(p) => p.$color || '#4338ca'};
`;

const toIsoDate = (date) => date.toISOString().slice(0, 10);

const ensureSeconds = (time) => {
  if (!time) return null;
  return time.length === 5 ? `${time}:00` : time;
};

const formatTime = (time) => {
  if (!time) return '— —';
  const normalized = ensureSeconds(time);
  if (!normalized) return '— —';

  const date = new Date(`1970-01-01T${normalized}`);
  if (Number.isNaN(date.getTime())) {
    return time.slice(0, 5);
  }

  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const parseDateTime = (dateStr, timeStr) => {
  const normalized = ensureSeconds(timeStr);
  if (!dateStr || !normalized) return null;
  const value = new Date(`${dateStr}T${normalized}`);
  return Number.isNaN(value.getTime()) ? null : value;
};

const formatDisplayDate = (iso) => {
  if (!iso) return '--';
  try {
    const date = new Date(`${iso}T00:00:00`);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  } catch (err) {
    console.error('Failed to format date', err);
    return iso;
  }
};

const relativeLabel = (targetIso, todayIso) => {
  if (!targetIso) return '';
  if (targetIso === todayIso) return 'Today';

  const base = new Date(`${todayIso}T00:00:00`);
  const target = new Date(`${targetIso}T00:00:00`);
  const diffDays = Math.round((target - base) / (1000 * 60 * 60 * 24));

  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) return `In ${diffDays} days`;
  if (diffDays < -1 && diffDays > -7) return `${Math.abs(diffDays)} days ago`;
  return '';
};

const shiftIsoDate = (iso, offset) => {
  let base = null;

  if (iso) {
    const parts = String(iso).split('-').map((p) => Number(p));
    if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
      const [year, month, day] = parts;
      base = new Date(year, month - 1, day);
    }
  }

  if (!base || Number.isNaN(base.getTime())) {
    base = new Date();
  }

  base.setDate(base.getDate() + offset);
  return toIsoDate(base);
};

const getSessionState = (session, selectedIso) => {
  const todayIso = toIsoDate(new Date());
  const base = {
    label: 'Scheduled',
    color: '#0ea5e9',
    bg: 'rgba(14, 165, 233, 0.12)',
  };

  if (selectedIso !== todayIso) return base;

  const now = new Date();
  const start = parseDateTime(selectedIso, session?.startTime);
  const end = parseDateTime(selectedIso, session?.endTime);

  if (start && now < start) {
    return { label: 'Upcoming', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' };
  }

  if (start && end && now >= start && now <= end) {
    return { label: 'In progress', color: '#f97316', bg: 'rgba(249, 115, 22, 0.15)' };
  }

  if (end && now > end) {
    return { label: 'Completed', color: '#16a34a', bg: 'rgba(22, 163, 74, 0.15)' };
  }

  return base;
};

export default function Timetable() {
  const todayIso = useMemo(() => toIsoDate(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [registration, setRegistration] = useState(null);
  const [registrationLoading, setRegistrationLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [schedulesError, setSchedulesError] = useState(null);
  const [otherEvents, setOtherEvents] = useState([]);
  const [otherEventsLoading, setOtherEventsLoading] = useState(false);

  useEffect(() => {
    setRegistrationLoading(true);
    try {
      const data = AuthService.getUser();
      setRegistration(data?.selectedRegistration || null);
    } finally {
      setRegistrationLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!registration?.batchId || !selectedDate) {
      setSchedules([]);
      setSchedulesError(null);
      setOtherEvents([]);
      return;
    }

    let cancelled = false;
    setSchedulesLoading(true);
    setSchedulesError(null);

    axiosInstance
      .get('/students/me/schedules', {
        params: { date: selectedDate, batchId: registration.batchId },
      })
      .then((resp) => {
        if (cancelled) return;
        const items = Array.isArray(resp?.data?.schedules) ? resp.data.schedules : [];
        setSchedules(items);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load schedules', error);
        const message =
          error?.response?.data?.message || 'Unable to load the timetable for this date. Please try again later.';
        setSchedulesError(message);
        setSchedules([]);
      })
      .finally(() => {
        if (!cancelled) setSchedulesLoading(false);
      });

    // Fetch other events in parallel
    setOtherEventsLoading(true);
    axiosInstance
      .get('/students/me/other-events', {
        params: { date: selectedDate, batchId: registration.batchId },
      })
      .then((resp) => {
        if (cancelled) return;
        setOtherEvents(Array.isArray(resp?.data?.events) ? resp.data.events : []);
      })
      .catch(() => {
        if (cancelled) return;
        setOtherEvents([]);
      })
      .finally(() => {
        if (!cancelled) setOtherEventsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [registration, selectedDate]);

  const sortedSchedules = useMemo(() => {
    if (!Array.isArray(schedules)) return [];
    return [...schedules].sort((a, b) => (a?.startTime || '').localeCompare(b?.startTime || ''));
  }, [schedules]);

  const handleDateChange = (event) => {
    const value = event.target.value;
    if (!value) return;
    setSelectedDate(value);
  };

  const moveBy = (offset) => {
    setSelectedDate((prev) => shiftIsoDate(prev || todayIso, offset));
  };

  return (
    <StudentLayout title="Timetable">
      <PageWrapper>
        <Heading>Plan your study days</Heading>
        <Description>
          Pick a date to see the sessions scheduled for your selected course and batch. Use the quick controls to jump
          between days and keep track of upcoming classes.
        </Description>

        <DateCard>
          <DateInfo>
            <DateEyebrow>{relativeLabel(selectedDate, todayIso) || 'Selected day'}</DateEyebrow>
            <DateHeading>{formatDisplayDate(selectedDate)}</DateHeading>
            <DateSub>
              {registration?.courseShortName || registration?.courseName || 'No course selected'}
            </DateSub>
          </DateInfo>

          <DateControls>
            <NavButton type="button" onClick={() => moveBy(-1)} aria-label="Previous day">
              ‹
            </NavButton>
            <DateInput type="date" value={selectedDate} onChange={handleDateChange} />
            <NavButton type="button" onClick={() => moveBy(1)} aria-label="Next day">
              ›
            </NavButton>
            <TodayButton type="button" disabled={selectedDate === todayIso} onClick={() => setSelectedDate(todayIso)}>
              Today
            </TodayButton>
          </DateControls>
        </DateCard>

        <StatGrid>
          <StatCard>
            <StatLabel>Course</StatLabel>
            <StatValue>{registration?.courseShortName || registration?.courseName || 'Not selected'}</StatValue>
            {registration?.courseName && registration?.courseShortName && (
              <StatHint>{registration.courseName}</StatHint>
            )}
          </StatCard>
          <StatCard>
            <StatLabel>Batch</StatLabel>
            <StatValue>{registration?.batchName || 'Not assigned'}</StatValue>
            <StatHint>{registration?.registrationNumber ? `Reg: ${registration.registrationNumber}` : '—'}</StatHint>
          </StatCard>
          <StatCard>
            <StatLabel>Sessions</StatLabel>
            <StatValue>
              {schedulesLoading ? '…' : sortedSchedules.length}
            </StatValue>
            <StatHint>{selectedDate === todayIso ? "Today's load" : 'Sessions on this day'}</StatHint>
          </StatCard>
          {otherEvents.length > 0 && (
            <StatCard>
              <StatLabel>Other Events</StatLabel>
              <StatValue>{otherEventsLoading ? '…' : otherEvents.length}</StatValue>
              <StatHint>Special events on this day</StatHint>
            </StatCard>
          )}
        </StatGrid>

        <ContentCard>
          <SectionHeading>Schedule for {formatDisplayDate(selectedDate)}</SectionHeading>
          <SectionSub>
            {registration?.batchName
              ? `Showing timetable for batch ${registration.batchName}.`
              : 'Select a course to see its timetable.'}
          </SectionSub>

          {registrationLoading && <Loader message="Loading your course selection…" />}

          {!registrationLoading && !registration && (
            <InfoBanner>
              We couldn&apos;t find an active course or batch for your profile. Please pick one from the course selection
              area to view its timetable.
            </InfoBanner>
          )}

          {!registrationLoading && registration && (
            <>
              {schedulesLoading && <Loader message="Fetching sessions for this day…" />}

              {!schedulesLoading && schedulesError && <ErrorCard>{schedulesError}</ErrorCard>}

              {!schedulesLoading && !schedulesError && sortedSchedules.length === 0 && otherEvents.length === 0 && (
                <EmptyCard>
                  No sessions are planned for this day.
                  <QuickTip>
                    If you expect a class, double-check with your course coordinator or try another date.
                  </QuickTip>
                </EmptyCard>
              )}

              {!schedulesLoading && !schedulesError && sortedSchedules.length === 0 && otherEvents.length > 0 && (
                <EmptyCard>
                  No regular classes on this day, but there {otherEvents.length === 1 ? 'is' : 'are'} {otherEvents.length} other event{otherEvents.length !== 1 ? 's' : ''} below.
                </EmptyCard>
              )}

              {!schedulesLoading && !schedulesError && sortedSchedules.length > 0 && (
                <SessionsList>
                  {sortedSchedules.map((session) => {
                    const state = getSessionState(session, selectedDate);
                    return (
                      <SessionCard key={session.scheduleId || `${session.subjectId || 'subject'}-${session.startTime}`}>
                        <SessionTime>
                          <div>{formatTime(session.startTime)}</div>
                          <div style={{ color: '#94a3b8', fontSize: 13 }}>to</div>
                          <div>{formatTime(session.endTime)}</div>
                        </SessionTime>
                        <SessionBody>
                          <SessionTitle>{session.subjectFullName || session.subjectShortName || 'Class'}</SessionTitle>
                          <SessionMeta>
                            {session.batchGroupName && <>Group: {session.batchGroupName} · </>}
                            {session.classroomName ? `Room: ${session.classroomName}` : 'Room not assigned'}
                          </SessionMeta>
                          <SessionBadges>
                            <StatusPill $color={state.color} $bg={state.bg}>
                              {state.label}
                            </StatusPill>
                            {session.staffName && <MetaChip>Lecturer: {session.staffName}</MetaChip>}
                            {session.mode && <MetaChip>Mode: {session.mode}</MetaChip>}
                            {session.deliveryType && <MetaChip>{session.deliveryType}</MetaChip>}
                          </SessionBadges>
                        </SessionBody>
                      </SessionCard>
                    );
                  })}
                </SessionsList>
              )}

              {/* Other Events */}
              {!otherEventsLoading && otherEvents.length > 0 && (
                <>
                  <div style={{ marginTop: 28, marginBottom: 16 }}>
                    <SectionHeading style={{ fontSize: 17 }}>Other Events</SectionHeading>
                    <SectionSub style={{ marginBottom: 0 }}>Special events scheduled for your batch on this day</SectionSub>
                  </div>
                  <SessionsList>
                    {otherEvents.map((ev) => (
                      <EventCard key={ev.eventId} $color={ev.eventTypeColor || '#6366f1'}>
                        <SessionTime>
                          <div>{formatTime(ev.startTime)}</div>
                          <div style={{ color: '#94a3b8', fontSize: 13 }}>to</div>
                          <div>{formatTime(ev.endTime)}</div>
                        </SessionTime>
                        <SessionBody>
                          <SessionTitle>{ev.title}</SessionTitle>
                          <SessionMeta>
                            {ev.classroomName ? `Room: ${ev.classroomName}` : 'No fixed room'}
                          </SessionMeta>
                          <SessionBadges>
                            {ev.eventTypeName && (
                              <EventTypeBadge
                                $color={ev.eventTypeColor || '#4338ca'}
                                $bg={ev.eventTypeColor ? `${ev.eventTypeColor}22` : 'rgba(99,102,241,0.12)'}
                              >
                                {ev.eventTypeName}
                              </EventTypeBadge>
                            )}
                            {ev.staff && ev.staff.length > 0 && (
                              <MetaChip>{ev.staff.join(', ')}</MetaChip>
                            )}
                          </SessionBadges>
                          {ev.notes && (
                            <div style={{ marginTop: 10, fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>
                              {ev.notes}
                            </div>
                          )}
                        </SessionBody>
                      </EventCard>
                    ))}
                  </SessionsList>
                </>
              )}
            </>
          )}
        </ContentCard>
      </PageWrapper>
    </StudentLayout>
  );
}
